import React, { FC, Fragment, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { TransportLine, TransportOrder } from 'models/transport';
import TransportSchedulePageActions from 'components/transport/transport-schedule/transport-schedule-page-actions.component';
import TransportDetailsDialog, {
    TransportOrderEdits,
} from 'components/transport/transport-details-dialog/transport-details-dialog.component';
import { useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import { revokeTransportTemplate, updateTransportTemplate } from 'apollo/templates/transport';
import SchedulePrintButton from 'components/transport/transport-schedule/schedule-print-button';
import { transportLinesTemplate } from 'apollo/templates/transport/query.graphql';
import TransportSchedule from 'components/transport/transport-schedule/transport-schedule.component';
import { NShiftShipmentWaybill } from 'models/nshift-shipment-waybill';
import { useLocation } from 'react-router-dom';
import WaybillsDetailsDialog from 'components/transport/waybills-details-dialog/waybills-details-dialog.component';

const TransportPlanner: FC = () => {
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [filterText, setFilterText] = useState<string>();
    const [currentTransportOrder, setCurrentTransportOrder] = useState<TransportOrder>();
    const [currentWaybills, setCurrentWaybills] = useState<NShiftShipmentWaybill[]>();
    const [isTransportDetailsOpen, setIsTransportDetailsOpen] = useState(false);
    const [isWaybillsDetailsOpen, setIsWaybillsDetailsOpen] = useState(false);
    const [allTransportLines, setAllTransportLines] = useState<TransportLine[]>([]);
    const [filteredTransportLines, setFilteredTransportLines] = useState<TransportLine[]>([]);
    const search = useLocation().search;
    const queryParams = new URLSearchParams(search);
    const date = queryParams.get('date');
    const editTransportId = queryParams.get('editTransportId');

    const setTransportOrder = (transportLines: TransportLine[]) => {
        const transportOrder = localStorage.getItem('transportLine');
        if (transportOrder) {
            setCurrentTransportOrder(JSON.parse(transportOrder || '{}'));
            return;
        }
        const transportOrders = transportLines.reduce((acc, transportLine) => {
            acc.push(...(transportLine.transportOrders || []));
            return acc;
        }, [] as TransportOrder[]);

        const tOrder = transportOrders.find((transportOrder) => {
            return transportOrder._id === editTransportId;
        });
        if (tOrder) {
            setCurrentTransportOrder(tOrder);
        }
    };

    const [transportLines, { refetch }] = useLazyQuery(transportLinesTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
        pollInterval: 1000 * 60 * 10,
        onCompleted: (data: { transportLines: TransportLine[] }) => {
            if (data.transportLines) {
                setAllTransportLines(data.transportLines);
                setFilteredTransportLines(data.transportLines);
            }
            if (date && editTransportId) {
                setTransportOrder(data.transportLines || []);
                setIsTransportDetailsOpen(true);
            }
        },
    });

    useEffect(() => {
        if (filterText) {
            setFilteredTransportLines(allTransportLines.filter((row) => row.name.includes(filterText)));
        }
    }, [filterText, allTransportLines]);

    useEffect(() => {
        if (selectedDate) {
            transportLines({
                variables: {
                    date: moment(selectedDate).format('yyyy-MM-DD'),
                },
            });
        }
    }, [selectedDate]);

    useEffect(() => {
        if (date) {
            const urlDate = new Date(parseInt(date));
            if (moment(urlDate).isValid()) {
                setSelectedDate(urlDate);
                transportLines({
                    variables: {
                        date: moment(urlDate).format('yyyy-MM-DD'),
                    },
                });
            }
        } else {
            setSelectedDate(new Date());
        }
    }, [date]);

    const [updateTransport] = useMutation(updateTransportTemplate, {
        onCompleted: () => {
            setIsTransportDetailsOpen(false);
            window.location.href = '/transport/transport-schedule';
        },
    });

    const [revokeTransport] = useMutation(revokeTransportTemplate, {
        onCompleted: () => {
            refetch?.();
            setIsTransportDetailsOpen(false);
        },
    });

    const handleDialogOkClick = (transportOrder: TransportOrder, changes: TransportOrderEdits) => {
        if (Object.keys(changes).length === 0) {
            setIsTransportDetailsOpen(false);
            return;
        }
        // DD-3443: send only what was edited — the backend `$set`s only these
        // fields, so values other users saved since this page loaded survive.
        updateTransport({
            variables: {
                transportOrderId: transportOrder._id,
                ...changes,
            },
        });
    };

    const handleDialogRevokeClick = (transportOrderId: string, selectedDate: Date | null) => {
        revokeTransport({
            variables: {
                transportOrderId: transportOrderId,
                userSelectedDate: moment(moment(selectedDate).toDate()).format('YYYY-MM-DD'),
            },
        });
    };

    return (
        <Fragment>
            <Box mb={2} display="flex" alignItems="center">
                <Box flexGrow={1}>
                    <TransportSchedulePageActions
                        onDateChange={(newDate) => setSelectedDate(newDate)}
                        onFilterTextChange={(newFilterText) => setFilterText(newFilterText)}
                    />
                </Box>
                <Box>
                    <SchedulePrintButton selectedDate={selectedDate} />
                </Box>
            </Box>
            <TransportSchedule
                selectedDate={selectedDate}
                transportLines={filteredTransportLines}
                onTransportClick={(transportOrder?: TransportOrder, waybills?: NShiftShipmentWaybill[]) => {
                    if (transportOrder) {
                        setCurrentTransportOrder(transportOrder);
                        setIsTransportDetailsOpen(true);
                    } else if (waybills) {
                        setCurrentWaybills(waybills);
                        setIsWaybillsDetailsOpen(true);
                    }
                }}
            />

            <TransportDetailsDialog
                transportOrder={currentTransportOrder}
                selectedDate={selectedDate}
                open={isTransportDetailsOpen}
                onOk={handleDialogOkClick}
                onClose={() => {
                    setIsTransportDetailsOpen(false);
                    setCurrentTransportOrder(undefined);
                }}
                onRevoke={handleDialogRevokeClick}
            />
            <WaybillsDetailsDialog
                open={isWaybillsDetailsOpen}
                waybills={currentWaybills}
                onClose={() => {
                    setIsWaybillsDetailsOpen(false);
                    setCurrentWaybills(undefined);
                }}
            />
        </Fragment>
    );
};

export default TransportPlanner;
